import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { ensureListing } from '../../util/data';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { hideTidioChat, showTidioChat } from '../../util/tidioChat';
import { cartLineItems as apiCartLineItems } from '../../util/api';
import {
  LINE_ITEM_PRINTEMPS_ROBOUP_DISCOUNT,
  LINE_ITEM_REOLINK_WEEKEND_2026_DISCOUNT,
  LINE_ITEM_T600MAI50_DISCOUNT,
} from '../../util/types';
import {
  buildProviderFrancoData,
  buildProviderGroupsFromCart,
  calculateProviderShippingCost,
  resolveShippingMethodIdForItem,
} from '../../util/cartProviderGroups';

const { Money } = sdkTypes;

import { H3, H4 } from '../Heading/Heading';
import NamedLink from '../NamedLink/NamedLink';
import IconDelete from '../IconDelete/IconDelete';
import { PrimaryButton, SecondaryButton } from '../Button/Button';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';

import {
  addMarketplaceEntities,
  getMarketplaceEntities,
} from '../../ducks/marketplaceData.duck';
import {
  updateCartItemQuantity,
  removeFromCart,
  updateProviderShippingMethod,
} from '../../ducks/cart.duck';
import ProviderShippingSelectors from '../ProviderShippingSelectors/ProviderShippingSelectors';

import css from './CartSidePanel.module.css';

const CartSidePanelLineItem = ({
  item,
  listing,
  providerId,
  onUpdateQuantity,
  onRemove,
  onCloseLink,
}) => {
  const intl = useIntl();
  const [isUpdating, setIsUpdating] = useState(false);

  const listingId = item.listingId?.uuid || item.listingId;
  const { quantity } = item;
  const price = listing?.attributes?.price;
  const title = listing?.attributes?.title || 'Product';
  const images = listing?.images || [];
  const firstImage = images.length > 0 ? images[0] : null;

  const isValidQuantity =
    typeof quantity === 'number' && !isNaN(quantity) && quantity > 0;
  const isValidPrice =
    price &&
    typeof price.amount === 'number' &&
    !isNaN(price.amount) &&
    price.amount > 0 &&
    price.currency;

  if (!isValidQuantity) {
    return null;
  }

  const itemTotal =
    isValidPrice && price.currency
      ? formatMoney(intl, new Money(price.amount * quantity, price.currency))
      : null;

  const handleQuantityChange = async newQuantity => {
    if (newQuantity === quantity) return;
    setIsUpdating(true);
    try {
      await onUpdateQuantity({ listingId, providerId, quantity: newQuantity });
    } catch (e) {
      // noop
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemove = async () => {
    setIsUpdating(true);
    try {
      await onRemove({ listingId, providerId });
    } catch (e) {
      // noop
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <li className={css.cartItem}>
      <div className={css.itemImage}>
        {firstImage ? (
          <ResponsiveImage
            rootClassName={css.productImage}
            alt={title}
            image={firstImage}
            variants={['listing-card', 'listing-card-2x']}
          />
        ) : (
          <div className={css.noImageThumb}>
            <FormattedMessage id="CartSidePanel.noImage" />
          </div>
        )}
      </div>

      <div className={css.itemDetails}>
        <div className={css.itemInfo}>
          <H4 className={css.itemTitle}>
            <NamedLink
              name="ListingPage"
              params={{ id: listingId, slug: createSlug(title) }}
              className={css.titleLink}
              onClick={onCloseLink}
            >
              {title}
            </NamedLink>
          </H4>
          {itemTotal ? <div className={css.itemTotal}>{itemTotal}</div> : null}
        </div>

        <div className={css.priceInfo}>
          {isValidPrice && price.currency ? (
            <div className={css.itemPrice}>
              <span className={css.unitPriceLabel}>
                <FormattedMessage id="CartPage.unitPrice" defaultMessage="Unit price" />
              </span>
              <span className={css.unitPriceValue}>{formatMoney(intl, price)}</span>
            </div>
          ) : null}
        </div>

        <div className={css.itemControls}>
          <div className={css.quantityControls}>
            <label className={css.quantityLabel}>
              <FormattedMessage id="CartPage.quantity" />
            </label>
            <div className={css.quantitySelector}>
              <button
                type="button"
                className={css.quantityBtn}
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={isUpdating || !isValidQuantity || quantity <= 1}
                aria-label={intl.formatMessage({ id: 'CartPage.decreaseQuantity' })}
              >
                −
              </button>
              <span className={css.quantityValue}>{isValidQuantity ? quantity : '—'}</span>
              <button
                type="button"
                className={css.quantityBtn}
                onClick={() => handleQuantityChange(quantity + 1)}
                disabled={isUpdating || !isValidQuantity}
                aria-label={intl.formatMessage({ id: 'CartPage.increaseQuantity' })}
              >
                +
              </button>
            </div>
          </div>

          <button
            type="button"
            className={css.removeBtn}
            onClick={handleRemove}
            disabled={isUpdating}
            aria-label={intl.formatMessage({ id: 'CartPage.removeItem' })}
          >
            <IconDelete className={css.removeIcon} />
            <FormattedMessage id="CartPage.remove" />
          </button>
        </div>
      </div>
    </li>
  );
};

const CartSidePanelComponent = props => {
  const {
    open,
    onClose,
    cart,
    listingsById,
    fetchMissingListings,
    onUpdateCartItemQuantity,
    onRemoveFromCart,
    onUpdateProviderShippingMethod,
    cartPromoCode,
    cartPromoCodes = [],
  } = props;

  const intl = useIntl();
  const config = useConfiguration();
  const history = useHistory();

  const providerGroups = buildProviderGroupsFromCart(cart, listingsById);
  const panelProviderFrancoData = buildProviderFrancoData(providerGroups, listingsById);

  const cartPromoCodesKey = (cartPromoCodes || []).join('|');

  const [estimated, setEstimated] = useState({
    lines: [],
    subtotal: 0,
    discount: 0,
    shipping: 0,
    total: 0,
  });

  const grandTotal = providerGroups.reduce((total, group) => {
    const groupTotal = group.items.reduce((groupSum, item) => {
      const listingId = item.listingId?.uuid || item.listingId;
      const listing = listingsById[listingId];
      const price = listing?.attributes?.price;
      const itemQuantity = item.quantity;

      const isValidPrice =
        price && typeof price.amount === 'number' && !isNaN(price.amount);
      const isValidQuantity =
        typeof itemQuantity === 'number' && !isNaN(itemQuantity) && itemQuantity > 0;

      return groupSum + (isValidPrice && isValidQuantity ? price.amount * itemQuantity : 0);
    }, 0);
    return total + groupTotal;
  }, 0);

  const totalItems = providerGroups.reduce((total, group) => {
    return (
      total +
      group.items.reduce((sum, item) => {
        const q = item.quantity;
        const isValidQuantity = typeof q === 'number' && !isNaN(q) && q > 0;
        return sum + (isValidQuantity ? q : 0);
      }, 0)
    );
  }, 0);

  useEffect(() => {
    if (!open) return;

    const cartObj = cart || {};
    const allRefs = Object.values(cartObj)
      .flatMap(providerData => {
        const items = Array.isArray(providerData)
          ? providerData
          : providerData.items || [];
        return items;
      })
      .map(it => {
        const uuid = it?.listingId?.uuid || it?.listingId;
        return { id: { uuid }, type: 'listing' };
      });

    if (allRefs.length > 0) {
      fetchMissingListings(allRefs);
    }
  }, [open, cart, fetchMissingListings]);

  useEffect(() => {
    if (!open) return;

    const pg = buildProviderGroupsFromCart(cart, listingsById);
    const pf = buildProviderFrancoData(pg, listingsById);

    const items = pg.flatMap(group =>
      group.items.map(it => {
        const listingId = it.listingId?.uuid || it.listingId;
        const shippingMethodId = resolveShippingMethodIdForItem(group, it);

        return {
          listingId,
          isOwnListing: false,
          orderData: {
            stockReservationQuantity: it.quantity,
            ...(it.deliveryMethod ? { deliveryMethod: it.deliveryMethod } : {}),
            ...(shippingMethodId ? { shippingMethodId } : {}),
          },
        };
      })
    );

    if (items.length === 0) {
      setEstimated({ lines: [], subtotal: 0, discount: 0, shipping: 0, total: 0 });
      return;
    }

    const promoCodesForRequest =
      cartPromoCodes && cartPromoCodes.length > 0
        ? cartPromoCodes
        : cartPromoCode
        ? [cartPromoCode]
        : [];

    apiCartLineItems(items, promoCodesForRequest)
      .then(({ data }) => {
        const lines = Array.isArray(data) ? data : [];
        const sum = filterFn =>
          lines
            .filter(li => {
              try {
                return filterFn(li);
              } catch {
                return false;
              }
            })
            .reduce((acc, li) => acc + (li?.lineTotal?.amount || 0), 0);

        const subtotal = sum(li => String(li.code || '').indexOf('line-item/item') === 0);
        const discount = sum(
          li =>
            li.code === LINE_ITEM_PRINTEMPS_ROBOUP_DISCOUNT ||
            li.code === LINE_ITEM_REOLINK_WEEKEND_2026_DISCOUNT ||
            li.code === LINE_ITEM_T600MAI50_DISCOUNT ||
            (typeof li.code === 'string' && li.code.startsWith('line-item/bundle-discount-'))
        );

        const providerShippingTotal = pg.reduce((sumShipping, group) => {
          const providerInfo = pf.find(p => p.providerId === group.providerId);
          return sumShipping + calculateProviderShippingCost(group, providerInfo);
        }, 0);

        const shipping = providerShippingTotal;
        const finalTotal = subtotal + shipping + discount;

        setEstimated({ lines, subtotal, discount, shipping, total: finalTotal });
      })
      .catch(() => {
        setEstimated({ lines: [], subtotal: 0, discount: 0, shipping: 0, total: 0 });
      });
  }, [
    open,
    JSON.stringify(cart),
    cartPromoCode,
    cartPromoCodesKey,
    Object.keys(listingsById).length,
  ]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    hideTidioChat();
    return () => showTidioChat();
  }, [open]);

  if (!open) {
    return null;
  }

  const firstCurrency =
    providerGroups[0]?.items[0] &&
    listingsById[providerGroups[0].items[0].listingId?.uuid || providerGroups[0].items[0].listingId]
      ?.attributes?.price?.currency;
  const currency =
    typeof firstCurrency === 'string' && firstCurrency.length > 0 ? firstCurrency : config.currency;

  const isValidCurrency = typeof currency === 'string' && currency.length > 0;

  /** Panier vide : pas de sous-total / livraison / total (évite « Gratuit » et valeurs API obsolètes) */
  const hasCartLines = totalItems > 0;

  const displaySubtotalAmount = hasCartLines ? estimated.subtotal || grandTotal : 0;

  const subtotalFormatted =
    hasCartLines &&
    isValidCurrency &&
    typeof displaySubtotalAmount === 'number' &&
    !isNaN(displaySubtotalAmount) &&
    displaySubtotalAmount > 0
      ? formatMoney(intl, new Money(displaySubtotalAmount, currency))
      : null;

  const fmtMoney = amt => {
    if (!isValidCurrency) return '—';
    try {
      const n = Number(amt);
      if (!Number.isFinite(n)) return '—';
      return formatMoney(intl, new Money(n, currency));
    } catch {
      return '—';
    }
  };

  const shippingAmount = hasCartLines ? estimated.shipping || 0 : 0;

  const totalAmount = hasCartLines
    ? estimated.total && estimated.total > 0
      ? estimated.total
      : grandTotal
    : 0;

  const totalFormatted =
    !hasCartLines
      ? null
      : isValidCurrency &&
          typeof totalAmount === 'number' &&
          !isNaN(totalAmount) &&
          totalAmount > 0
        ? formatMoney(intl, new Money(totalAmount, currency))
        : subtotalFormatted;

  const handleCheckout = () => {
    history.push('/cart');
    onClose();
  };

  return (
    <>
      <div
        className={css.overlay}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={css.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-side-panel-title"
      >
        <div className={css.header}>
          <div className={css.headerTitleRow}>
            <H3 className={css.title} id="cart-side-panel-title">
              <FormattedMessage id="CartSidePanel.title" />{' '}
              <span className={css.itemCountParen}>
                (
                <FormattedMessage id="CartSidePanel.itemCount" values={{ count: totalItems }} />)
              </span>
            </H3>
          </div>
          <button
            type="button"
            className={css.closeBtn}
            onClick={onClose}
            aria-label={intl.formatMessage({ id: 'CartSidePanel.close' })}
          >
            ×
          </button>
        </div>

        <div className={css.scrollArea}>
          {providerGroups.map(group => {
            const firstListingId = group.items[0]?.listingId?.uuid || group.items[0]?.listingId;
            const firstListing = firstListingId ? listingsById[firstListingId] : null;
            const sellerName =
              firstListing?.author?.attributes?.profile?.displayName ||
              firstListing?.author?.attributes?.profile?.firstName ||
              intl.formatMessage({ id: 'CartPage.seller' });

            return (
              <div key={group.providerId} className={css.providerGroup}>
                <div className={css.providerHeader}>
                  <FormattedMessage id="CartSidePanel.soldBy" />
                  <span className={css.providerName}>{sellerName}</span>
                </div>
                <ul className={css.itemList}>
                  {group.items.map((item, idx) => {
                    const listingId = item.listingId?.uuid || item.listingId;
                    const listing = ensureListing(listingsById[listingId]);

                    if (!listing || !listing.id) {
                      return null;
                    }

                    return (
                      <CartSidePanelLineItem
                        key={`${listingId}_${idx}`}
                        item={item}
                        listing={listing}
                        providerId={group.providerId}
                        onUpdateQuantity={onUpdateCartItemQuantity}
                        onRemove={onRemoveFromCart}
                        onCloseLink={onClose}
                      />
                    );
                  })}
                </ul>
                <ProviderShippingSelectors
                  group={group}
                  providerSubtotal={
                    panelProviderFrancoData.find(p => p.providerId === group.providerId)
                      ?.providerSubtotal || 0
                  }
                  currency={config.currency}
                  onUpdateShippingMethod={onUpdateProviderShippingMethod}
                  idPrefix="cart-side-panel-shipping"
                />
              </div>
            );
          })}
        </div>

        <div className={css.footer}>
          <div className={css.summaryRows}>
            <div className={css.summaryRow}>
              <span className={css.summaryLabel}>
                <FormattedMessage id="CartSidePanel.subtotal" />
              </span>
              <span className={css.summaryValue}>{subtotalFormatted || '—'}</span>
            </div>

            <div className={css.summaryRow}>
              <span className={css.summaryLabel}>
                <FormattedMessage id="CartPage.shipping" />
              </span>
              <span className={css.summaryValue}>
                {!hasCartLines ? (
                  '—'
                ) : shippingAmount <= 0 ? (
                  <span className={css.freeShipping}>
                    <FormattedMessage id="CartPage.freeShipping" defaultMessage="Gratuit" />
                  </span>
                ) : (
                  fmtMoney(shippingAmount)
                )}
              </span>
            </div>

            {hasCartLines &&
              (estimated.discount || 0) < 0 &&
              (() => {
                const lines = estimated.lines || [];
                const bundleDiscountLines = lines.filter(
                  li =>
                    typeof li.code === 'string' &&
                    li.code.startsWith('line-item/bundle-discount-')
                );
                const hardcodedDiscountLines = lines.filter(
                  li =>
                    li.code === LINE_ITEM_PRINTEMPS_ROBOUP_DISCOUNT ||
                    li.code === LINE_ITEM_REOLINK_WEEKEND_2026_DISCOUNT ||
                    li.code === LINE_ITEM_T600MAI50_DISCOUNT
                );
                const hardcodedAmount = hardcodedDiscountLines.reduce(
                  (acc, li) => acc + (li?.lineTotal?.amount || 0),
                  0
                );

                return (
                  <>
                    {bundleDiscountLines.map(li => {
                      const packName =
                        (li.metadata && String(li.metadata.bundleTitle || '').trim()) ||
                        intl.formatMessage({
                          id: 'CartPage.bundleDiscountFallback',
                          defaultMessage: 'Pack',
                        });
                      return (
                        <div key={li.code} className={css.summaryRow}>
                          <span className={css.summaryLabel}>
                            <FormattedMessage
                              id="CartPage.bundleDiscountRow"
                              defaultMessage="Réduction {packName}"
                              values={{ packName }}
                            />
                          </span>
                          <span className={css.discountValue}>
                            {fmtMoney(li?.lineTotal?.amount || 0)}
                          </span>
                        </div>
                      );
                    })}
                    {hardcodedAmount < 0 ? (
                      <div className={css.summaryRow}>
                        <span className={css.summaryLabel}>
                          <FormattedMessage id="CartPage.promoDiscount" />
                        </span>
                        <span className={css.discountValue}>{fmtMoney(hardcodedAmount)}</span>
                      </div>
                    ) : null}
                    {bundleDiscountLines.length === 0 && hardcodedAmount >= 0 ? (
                      <div className={css.summaryRow}>
                        <span className={css.summaryLabel}>
                          <FormattedMessage id="CartPage.promoDiscount" />
                        </span>
                        <span className={css.discountValue}>{fmtMoney(estimated.discount)}</span>
                      </div>
                    ) : null}
                  </>
                );
              })()}

            <div className={css.summaryDivider} />

            <div className={css.summaryTotalRow}>
              <span className={css.summaryTotalLabel}>
                <FormattedMessage id="CartPage.total" />
              </span>
              <span className={css.summaryTotalValue}>{totalFormatted || '—'}</span>
            </div>
          </div>

          <PrimaryButton
            type="button"
            className={css.fullWidthBtn}
            onClick={handleCheckout}
          >
            <FormattedMessage id="CartSidePanel.checkout" />
          </PrimaryButton>
          <SecondaryButton
            type="button"
            className={css.fullWidthBtn}
            onClick={onClose}
          >
            <FormattedMessage id="CartSidePanel.continueShopping" />
          </SecondaryButton>
        </div>
      </aside>
    </>
  );
};

const mapStateToProps = state => {
  const { cart = {} } = state.Cart || {};
  const refs = Object.values(cart)
    .flatMap(providerData => {
      const items = Array.isArray(providerData)
        ? providerData
        : providerData.items || [];
      return items;
    })
    .map(it => ({ id: { uuid: it?.listingId?.uuid || it?.listingId }, type: 'listing' }))
    .filter(ref => ref?.id?.uuid);

  const listings = getMarketplaceEntities(state, refs) || [];
  const listingsById = listings.reduce((m, l) => ({ ...m, [l?.id?.uuid]: l }), {});

  return {
    cart,
    cartPromoCode: state.Cart?.cartPromoCode ?? null,
    cartPromoCodes: state.Cart?.cartPromoCodes ?? [],
    listingsById,
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateCartItemQuantity: params => dispatch(updateCartItemQuantity(params)),
  onRemoveFromCart: params => dispatch(removeFromCart(params)),
  onUpdateProviderShippingMethod: params =>
    dispatch(updateProviderShippingMethod(params)),
  fetchMissingListings: refs =>
    dispatch((innerDispatch, getState, sdk) => {
      const state = getState();
      const existing = getMarketplaceEntities(state, refs);
      const existingIds = new Set((existing || []).map(l => l?.id?.uuid));
      const toFetch = refs.map(r => r?.id?.uuid).filter(uuid => uuid && !existingIds.has(uuid));

      const configDefault = require('../../config/configDefault').default;
      const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
        configDefault.layout.listingImage;
      const aspectRatio = aspectHeight / aspectWidth;

      const promises = Array.from(new Set(toFetch)).map(uuid =>
        sdk.listings.show({
          id: uuid,
          include: ['author', 'images'],
          'fields.image': [
            'variants.scaled-small',
            'variants.scaled-medium',
            `variants.${variantPrefix}`,
            `variants.${variantPrefix}-2x`,
          ],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        })
      );
      // allSettled: a stale/deleted cart listing (404) must not drop the ones
      // that resolved.
      return Promise.allSettled(promises).then(results => {
        results.forEach(r => {
          if (r.status === 'fulfilled' && r.value) {
            innerDispatch(addMarketplaceEntities(r.value));
          }
        });
      });
    }),
});

const CartSidePanel = compose(
  connect(mapStateToProps, mapDispatchToProps)
)(CartSidePanelComponent);

export default CartSidePanel;
